import React, {useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import Select from '@material-ui/core/Select';
import {useDispatch, useSelector} from 'react-redux';
import moment from 'moment/moment';
import ListItem from '@material-ui/core/ListItem';
import {
  getBogoItems,
  getCurrentDiscounts,
  getEmailMarketingToggleError,
  getEmailMarketingTrialDate,
  getIsEmailMarketingTurnOffModalOpen,
  getLoadingBogoItems
} from 'store/discountsV2/selectors';
import {useHistory, useLocation} from 'react-router-dom';
import {
  useDaysPickerContext,
  useDaysPickerContextUpdate
} from 'routes/App/discountsV2/DiscountWizard/DateAvailability/context/DaysPickerContext';
import {
  CLEAR_DISCOUNT,
  GET_BOGO_ITEMS,
  OPEN_EMAIL_MARKETING_TURN_OFF_MODAL,
  SET_DISCOUNT_MODAL,
  SET_EMAIL_MARKETING_TOGGLE_ERROR
} from 'store/discountsV2/action';
import DialogContainer from 'components/DialogContainer/styles';
import DialogClose from 'components/DialogClose/styles';
import {
  DashboardField,
  DashboardFieldDatepicker,
  DashboardFieldInner,
  DashboardFieldLabel,
  DashboardFields,
  DashboardInnerDescription,
  DialogBody,
  FieldInput,
  FieldInputDollar,
  FieldLabel
} from 'routes/App/dashboard/styles';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from 'components/DialogTitle/styles';
import DialogSubTitle from 'components/DialogSubTitle/styles';
import {Box, Checkbox, FormControl, FormControlLabel} from '@material-ui/core';
import {
  DISCOUNT_POSTHOG_EVENTS,
  DISCOUNT_STATUS,
  DISCOUNT_TYPE
} from 'helpers/constants/discountsV2';
import {ErrorSign, ErrorSignWrapper} from 'components/errorSign/errorSign';
import {ButtonInfo, ButtonPrimary} from 'components/buttons';
import {twoDecimalsFormatter} from 'helpers/functionUtils/helpFunctions';
import {
  DiscountsModalButton,
  DiscountsModalSwitch
} from 'routes/App/discountsV2/styles';
import SwitchS2E from 'components/Switch';

import isEmailMarketingTrialExpired from 'helpers/functionUtils/emailMarketing';
import EmailMarketingTurnOffModal from 'routes/App/discountsV2/DiscountsList/Modals/EmailMarketingTurnOffModal';
import DatepickerS2E from 'components/Datepicker';
import DaysPickerContainer from 'routes/App/discountsV2/DiscountWizard/DateAvailability/DaysPickerContainer';
import {
  convertScheduleFromServiceFormat,
  convertScheduleToServiceFormat
} from 'routes/App/discountsV2/DiscountWizard/DateAvailability/helpers';
import SelectWrapper from 'components/select/SelectComponentStyles';
import Loading from 'components/shared/Utils/Loading';
import {FlexContainer, FlexItem} from 'components/layout';
import SearchField from 'components/fields/SearchField';

const BogoSettingsModal = (props) => {
  const {
    formik,
    discount,
    enabledDiscount,
    setEnabledDiscount,
    enabledDate,
    setEnabledDate,
    buttonStatus,
    allowedServiceFeatures,
    enabledCustomAvailability,
    setEnabledCustomAvailability,
    enabledAdvancedSettings,
    setEnabledAdvancedSettings,
    isOpen,
    onCloseAction
  } = props;

  const dispatch = useDispatch();
  const {activeLocation, userData} = useSelector((state) => state.user);
  const isEmailMarketingToggleError = getEmailMarketingToggleError();
  const isEmailMarketingTurnOffModalOpen = getIsEmailMarketingTurnOffModalOpen();
  const emailMarketingTrialDate = getEmailMarketingTrialDate();
  const {Timezone} = useSelector((state) => state.user.activeLocation);
  const visualChecked = !formik.values.isPromotional;
  const bogoItems = getBogoItems();
  const loadingBogoItems = getLoadingBogoItems();
  const currentDiscounts = getCurrentDiscounts();
  const [selectedBogoItems, setSelectedBogoItems] = useState([]);
  const [filteredBogoItems, setFilteredBogoItems] = useState([]);
  const [search, setSearch] = useState('');
  const [openSelect, setOpenSelect] = useState(false);

  const daysPickerContext = useDaysPickerContext();
  const setDaysPickerContext = useDaysPickerContextUpdate();

  useEffect(() => {
    if (bogoItems?.length > 0) {
      setFilteredBogoItems(bogoItems);
      if (formik?.values?.bogo?.discounted?.length > 0) {
        setSelectedBogoItems(
          [formik?.values?.bogo?.discounted[0]?.posId] || []
        );
      } else {
        setSelectedBogoItems([]);
      }
    }
  }, [bogoItems]);

  useEffect(() => {
    if (!bogoItems || bogoItems.length === 0) {
      dispatch(GET_BOGO_ITEMS());
    }

    return () => {
      dispatch(CLEAR_DISCOUNT());
    };
  }, []);

  const handleSearchBogoItems = (value) => {
    setSearch(value);
    if (bogoItems.length > 0) {
      setFilteredBogoItems(
        bogoItems.filter((item) =>
          item.name.toLowerCase().includes(value.toLowerCase())
        )
      );
    } else {
      setFilteredBogoItems(bogoItems);
    }
  };

  const handleBogoItemsSelect = (value) => {
    if (value) {
      setSelectedBogoItems([value.posId]);
      formik.setFieldValue('bogo.discounted', [value]);
    }
  };

  const handleOpen = () => {
    setOpenSelect(!openSelect);
  };

  const handleSearchClear = () => {
    setSearch('');
    setFilteredBogoItems(bogoItems);
  };

  const discountMap = useMemo(() => {
    const map = new Map();

    if (!currentDiscounts || currentDiscounts.length === 0) {
      return map;
    }

    currentDiscounts.forEach((currentDiscount) => {
      currentDiscount.items.forEach((item) => {
        if (!map.has(item.posId)) {
          map.set(item.posId, []);
        }
        map.get(item.posId).push({
          discountId: currentDiscount.discountId,
          discountName: currentDiscount.discountName
        });
      });
    });
    return map;
  }, [currentDiscounts]);

  useEffect(() => {
    if (isOpen && discount && window?.posthog) {
      window?.posthog.capture(DISCOUNT_POSTHOG_EVENTS.MODAL_OPENED, {
        mode: discount?.id ? 'edit' : 'new',
        discount_type: 'bogo',
        location_id: activeLocation?._id,
        location_name: activeLocation?.BusinessName || activeLocation?.FullName,
        user_id: userData?._id,
        user_email: userData?.email,
        timestamp: moment().utc().format()
      });
    }
  }, [isOpen, discount]);

  return (
    <Dialog
      open={isOpen}
      onClose={onCloseAction}
      disableEnforceFocus
      disableRestoreFocus
      scroll='body'
    >
      <DialogContainer className='DialogLgDiscounts'>
        <DialogClose onClick={onCloseAction} />
        <Box>
          <DialogTitle className='marginBottomLg'>
            {discount?.id ? 'Edit ' : 'Create New '}Discount - BOGO
          </DialogTitle>
        </Box>
        <DialogBody>
          <DashboardFields>
            <DashboardField className='discountV2Name dFlex'>
              <DashboardFieldInner className='flexAuto DiscountsV2 FieldRelative'>
                <FieldLabel
                  className={cn('FieldLabelLg mBottomMd', {
                    isDisabled: discount.status === DISCOUNT_STATUS.ARCHIVED
                  })}
                >
                  Discount Name <span className='redText'>*</span>
                </FieldLabel>
                <FieldInput
                  className={cn('', {
                    error: formik.errors?.name && formik.touched?.name,
                    isDisabled: discount.status === DISCOUNT_STATUS.ARCHIVED
                  })}
                  name='name'
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.name}
                  placeholder='Discount Name'
                />
                <ErrorSignWrapper className='ErrorSignWrapper top3'>
                  {formik.errors?.name && formik.touched?.name ? (
                    <ErrorSign>{formik.errors.name}</ErrorSign>
                  ) : null}
                </ErrorSignWrapper>
              </DashboardFieldInner>
            </DashboardField>
          </DashboardFields>
          <DashboardField className='discountV2Name paddingL0 paddingR0'>
            <FieldLabel
              className={cn('FieldLabelLg mBottomMd', {
                isDisabled: discount.status === DISCOUNT_STATUS.ARCHIVED
              })}
            >
              Select item <span className='redText'>*</span>
            </FieldLabel>
            <SelectWrapper className='SelectMd w100Auto AlignCenter'>
              <FormControl className='select-formcontrol DiscountsV2'>
                <Select
                  value={selectedBogoItems.length > 0 ? selectedBogoItems : []}
                  name='bogo.discounted'
                  open={openSelect}
                  multiple
                  displayEmpty
                  onOpen={handleOpen}
                  onClose={handleOpen}
                  onChange={() => {}}
                  className={formik?.errors?.bogo?.discounted && 'error'}
                  renderValue={(selected) => {
                    if (!selected || selected.length === 0) {
                      return <span style={{color: '#aaa'}}>Select item</span>;
                    }
                    return selected
                      .map(
                        (item) =>
                          bogoItems.find((el) => item === el.posId)?.name
                      )
                      ?.join(', ');
                  }}
                  MenuProps={{
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'left'
                    },
                    transformOrigin: {
                      vertical: 'top',
                      horizontal: 'left'
                    },
                    PaperProps: {
                      style: {
                        maxHeight: 220,
                        overflowY: 'auto',
                        maxWidth: 250
                      }
                    },
                    getContentAnchorEl: null
                  }}
                >
                  {loadingBogoItems ? (
                    <Loading />
                  ) : (
                    [
                      <div
                        key='search-container'
                        style={{
                          position: 'sticky',
                          top: 0,
                          background: 'white',
                          zIndex: 100
                        }}
                      >
                        <FlexContainer>
                          <FlexItem
                            className='padding10 bogoSearch'
                            flex='1 1 auto'
                          >
                            <SearchField
                              placeholder='Search by item'
                              value={search}
                              onChange={(e) =>
                                handleSearchBogoItems(e.target.value)
                              }
                              onClear={handleSearchClear}
                              isClearIconVisible={search?.length > 0}
                            />
                          </FlexItem>
                        </FlexContainer>
                      </div>,
                      ...filteredBogoItems?.map((item) => {
                        const existingDiscounts =
                          discountMap.get(item.posId) || [];

                        const isUsedInCurrent = existingDiscounts.some(
                          (entry) => entry.discountId === discount?.id
                        );

                        const isUsedInOthers = existingDiscounts.some(
                          (entry) => entry.discountId !== discount?.id
                        );

                        const isDisabled = isUsedInOthers && !isUsedInCurrent;

                        const otherDiscountNames = existingDiscounts
                          .filter((entry) => entry.discountId !== discount?.id)
                          .map((entry) => entry.discountName)
                          .join(', ');

                        return (
                          <ListItem
                            className='listItemText'
                            onClick={() => {
                              if (!isDisabled) {
                                handleBogoItemsSelect(item);
                                handleOpen();
                                handleSearchClear();
                              }
                            }}
                            key={item.posId}
                            value={item.posId}
                            disabled={isDisabled}
                            title={
                              otherDiscountNames
                                ? `Already in: ${otherDiscountNames}`
                                : ''
                            }
                          >
                            {item.name}
                          </ListItem>
                        );
                      })
                    ]
                  )}
                </Select>
              </FormControl>
            </SelectWrapper>
            <ErrorSignWrapper topPosition='-9%' leftPosition='0'>
              <ErrorSign>
                {formik?.errors?.bogo?.discounted &&
                  formik.errors.bogo.discounted}
              </ErrorSign>
            </ErrorSignWrapper>
          </DashboardField>
          {/* {(allowedServiceFeatures.includes('email_campaigns_active') ||
            (emailMarketingTrialDate &&
              !isEmailMarketingTrialExpired(
                emailMarketingTrialDate,
                Timezone
              ))) && (
            <DiscountsModalSwitch>
              <div className='LeftBlock'>
                <SwitchS2E
                  labelPlacement='start'
                  checked={formik.values?.marketing?.email?.isEnabled}
                  onChange={(e) => {
                    if (!e.target.checked) {
                      dispatch(OPEN_EMAIL_MARKETING_TURN_OFF_MODAL(true));
                    } else {
                      formik.setFieldValue('marketing.email.isEnabled', true);
                    }
                  }}
                  name='marketing.email.isEanbled'
                  isDisabled={discount.status === DISCOUNT_STATUS.ARCHIVED}
                />
                <span className='switcher-text'>
                  {formik.values?.marketing?.email?.isEnabled
                    ? 'Active'
                    : 'Disabled'}
                </span>
              </div>
              <div className='RightBlock'>
                <div className='title'>Include in Email Marketing</div>
                <div className='description'>
                  Display this discount in your email marketing campaigns
                </div>
                <ErrorSignWrapper className='ErrorSignWrapper noWrap'>
                  {formik.errors?.marketing?.email?.isEnabled &&
                  formik.touched?.marketing?.email?.isEnabled ? (
                    <ErrorSign>
                      {formik.errors.marketing.email.isEnabled}
                    </ErrorSign>
                  ) : null}
                </ErrorSignWrapper>
              </div>
            </DiscountsModalSwitch>
          )} */}
          <DiscountsModalSwitch className='mTop17'>
            <div className='LeftBlock'>
              <SwitchS2E
                labelPlacement='start'
                checked={enabledCustomAvailability}
                onChange={(e) => {
                  if (!e.target.checked) {
                    setDaysPickerContext([]);
                    formik.setFieldValue('availability.activatesAtLocal', null);
                    formik.setFieldValue(
                      'availability.deactivatesAtLocal',
                      null
                    );
                    setEnabledDate(false);
                    setEnabledAdvancedSettings(false);
                    formik.setFieldValue('availability.weekly', null);
                  }
                  setEnabledCustomAvailability(e.target.checked);
                }}
                name='enabledCustomAvailability'
                isDisabled={discount.status === DISCOUNT_STATUS.ARCHIVED}
              />
              <span className='switcher-text'>
                {enabledCustomAvailability ? 'Active' : 'Disabled'}
              </span>
            </div>
            <div className='RightBlock'>
              <div className='title'>Custom availability</div>
              <div className='description'>
                Set specific date, dates and hour
              </div>
            </div>
          </DiscountsModalSwitch>
          {enabledCustomAvailability && (
            <>
              <DashboardField className='dFlex w100 DiscountDatepicker paddingR0 paddingL0'>
                <FormControlLabel
                  className='Say2EatCheckbox'
                  control={
                    <Checkbox
                      labelStyle={{color: '#2F3D48'}}
                      iconStyle={{fill: '#FF2D38'}}
                      inputStyle={{color: '#FF2D38'}}
                      style={{
                        color: '#FF2D38',
                        opacity:
                          discount.status === DISCOUNT_STATUS.ARCHIVED
                            ? '0.3'
                            : null
                      }}
                      onChange={(e) => {
                        const {checked} = e.target;
                        setEnabledDate(checked);
                        if (!checked) {
                          formik.setFieldValue(
                            'availability.activatesAtLocal',
                            null
                          );
                          formik.setFieldValue(
                            'availability.deactivatesAtLocal',
                            null
                          );
                        }
                      }}
                      checked={enabledDate}
                      disabled={discount.status === DISCOUNT_STATUS.ARCHIVED}
                    />
                  }
                  label='From'
                />
                <DashboardFieldDatepicker>
                  <DatepickerS2E
                    hideTextFrom
                    disablePast
                    disabled={
                      !enabledDate ||
                      discount.status === DISCOUNT_STATUS.ARCHIVED
                    }
                    value={[
                      formik.values?.availability?.activatesAtLocal
                        ? moment(formik.values.availability.activatesAtLocal)
                            .utc()
                            .format('MM/DD/yy')
                        : null,
                      formik.values?.availability?.deactivatesAtLocal
                        ? moment(formik.values.availability.deactivatesAtLocal)
                            .utc()
                            .format('MM/DD/yy')
                        : null
                    ]}
                    onClose={(value) => {
                      if (value && value.length) {
                        formik.setFieldValue(
                          'availability.activatesAtLocal',
                          moment(value[0])
                            .utcOffset(0, true)
                            .startOf('day')
                            .format()
                        );
                        formik.setFieldValue(
                          'availability.deactivatesAtLocal',
                          moment(value[1])
                            .utcOffset(0, true)
                            .endOf('day')
                            .format()
                        );
                      }
                    }}
                    clearSelectedValue
                  />
                </DashboardFieldDatepicker>
                <ErrorSignWrapper
                  className='ErrorSignWrapper'
                  leftPosition='93px'
                  topPosition='-14%'
                >
                  {formik.errors?.availability?.activatesAtLocal &&
                  formik.touched?.availability?.activatesAtLocal ? (
                    <ErrorSign>
                      {formik.errors.availability.activatesAtLocal}
                    </ErrorSign>
                  ) : null}
                </ErrorSignWrapper>
              </DashboardField>

              <DashboardField className='dFlex w100 DiscountDatepicker paddingR0 paddingL0'>
                <FormControlLabel
                  className='Say2EatCheckbox'
                  control={
                    <Checkbox
                      labelStyle={{color: '#2F3D48'}}
                      iconStyle={{fill: '#FF2D38'}}
                      inputStyle={{color: '#FF2D38'}}
                      style={{
                        color: '#FF2D38',
                        opacity:
                          discount.status === DISCOUNT_STATUS.ARCHIVED
                            ? '0.3'
                            : null
                      }}
                      onChange={(e) => {
                        const {checked} = e.target;
                        setEnabledAdvancedSettings(checked);
                        if (!checked) {
                          formik.setFieldValue('availability.weekly', null);
                          setDaysPickerContext([]);
                        }
                      }}
                      checked={enabledAdvancedSettings}
                      disabled={discount.status === DISCOUNT_STATUS.ARCHIVED}
                    />
                  }
                  label='Advanced settings'
                />
              </DashboardField>

              {enabledAdvancedSettings && (
                <DashboardField className='paddingR0 paddingL0'>
                  <DaysPickerContainer
                    WorkingDays={convertScheduleFromServiceFormat(
                      formik?.values?.availability?.weekly || []
                    )}
                  />
                </DashboardField>
              )}
            </>
          )}
          <DiscountsModalButton>
            <ButtonPrimary
              type='button'
              className={cn('ButtonDashboard', {
                disabled:
                  buttonStatus || discount.status === DISCOUNT_STATUS.ARCHIVED
              })}
              onClick={(e) => {
                e.stopPropagation();
                setEnabledDiscount(false);

                formik.setFieldValue('promoCode', null);
                formik.setFieldValue('subtotal.min', 0);

                formik.setFieldValue(
                  'availability.weekly',
                  enabledAdvancedSettings
                    ? convertScheduleToServiceFormat(daysPickerContext)
                    : null
                );

                formik.handleSubmit(e);
              }}
            >
              <span>Save</span>
            </ButtonPrimary>
          </DiscountsModalButton>
        </DialogBody>
      </DialogContainer>
      {isEmailMarketingTurnOffModalOpen && (
        <EmailMarketingTurnOffModal
          isOpen={isEmailMarketingTurnOffModalOpen}
          setFieldValue={formik.setFieldValue}
        />
      )}
    </Dialog>
  );
};

BogoSettingsModal.propTypes = {
  formik: PropTypes.object.isRequired,
  discount: PropTypes.object.isRequired,
  enabledDiscount: PropTypes.bool.isRequired,
  setEnabledDiscount: PropTypes.func.isRequired,
  enabledDate: PropTypes.bool.isRequired,
  setEnabledDate: PropTypes.func.isRequired,
  buttonStatus: PropTypes.bool.isRequired,
  allowedServiceFeatures: PropTypes.array.isRequired,
  enabledCustomAvailability: PropTypes.bool.isRequired,
  setEnabledCustomAvailability: PropTypes.func.isRequired,
  enabledAdvancedSettings: PropTypes.bool.isRequired,
  setEnabledAdvancedSettings: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onCloseAction: PropTypes.func.isRequired
};

export default BogoSettingsModal;
